import type { DragEvent } from 'react';
import { memo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { MdOutlineDragIndicator } from 'react-icons/md';
import { Button, cn, FormTextArea, Insight } from '@/core/ui';
import type { IStationSchema } from './schema';

const _ExaminerQuestions = ({ className, showInfo = true }: { className?: string; showInfo?: boolean }) => {
  const { control, getValues, setValue } = useFormContext<IStationSchema>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'examinerQuestions',
  });

  const reindexQuestions = () => {
    const current = getValues('examinerQuestions') ?? [];
    setValue(
      'examinerQuestions',
      current.map((question, index) => ({ ...question, index: index + 1 })),
      { shouldDirty: true, shouldTouch: true, shouldValidate: true },
    );
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('index', String(index));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData('index'));
    if (Number.isNaN(from) || from === index) return;
    move(from, index);
    requestAnimationFrame(reindexQuestions);
  };

  return (
    <div className={cn('space-y-4 opacity-50 cursor-not-allowed', className)} inert>
      <div className='flex flex-row items-center justify-between gap-2'>
        <span className='text-default-500 text-sm'>Examiner Questions ({fields.length})</span>
        <Button
          size='sm'
          variant='bordered'
          className='gap-2'
          onPress={() => append({ index: fields.length + 1, question: '' })}
          aria-label='Add examiner question'
        >
          <BsPlus className='size-4' />
          Add
        </Button>
      </div>

      {showInfo && (
        <Insight
          variant='faded'
          title='Examiner questions'
          color='secondary'
          description='Add prompts for the examiner. Drag to reorder, remove unused items, and keep each question concise.'
        />
      )}

      {fields.length === 0 && (
        <div className='text-default-400 text-sm border border-dashed rounded-lg p-4 text-center'>
          No examiner questions yet. Click &ldquo;Add&rdquo; to create one.
        </div>
      )}

      <div className='space-y-2'>
        {fields.map((field, index) => (
          <div
            key={field.id}
            className='flex items-start gap-2 cursor-move'
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div className='flex flex-col gap-1 pt-1'>
              <span className='cursor-grab active:cursor-grabbing bg-warning-50 p-1 border border-warning-100 rounded text-sm text-warning-500 size-6'>
                <MdOutlineDragIndicator className='size-3.5' />
              </span>
              <div className='flex items-center justify-center bg-default-100 p-0.5 border border-default-200 rounded text-xs text-default-500 min-w-6 h-6'>
                #{index + 1}
              </div>
            </div>

            <div className='flex-1'>
              <FormTextArea
                isRequired
                id={`examinerQuestions.${index}.question`}
                control={control}
                name={`examinerQuestions.${index}.question`}
                variant='faded'
                maxRows={2}
                className='text-sm'
                placeholder='Enter examiner question'
              />
            </div>

            <Button
              size='sm'
              color='danger'
              isIconOnly
              variant='faded'
              onPress={() => {
                remove(index);
                requestAnimationFrame(reindexQuestions);
              }}
              aria-label='Delete examiner question'
            >
              <BsTrash className='size-3' />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ExaminerQuestions = memo(_ExaminerQuestions);
